import { Label } from '@/components/ui';
import { cn } from '@/helpers/ui-utils';
import * as React from 'react';

type PrefixAndSuffixBaseProps = {
  /**
   * A React node (e.g., element, string, or component) to be rendered
   * as a prefix or suffix.
   */
  component: React.ReactNode;
};

type SuffixOptions = {
  /**
   * Configuration options for the prefix and suffix.
   */
  options?: {
    /**
     * Controls visibility behavior of the prefix/suffix element.
     * When `true`, the element is initially hidden and only becomes visible
     * when the input is focused or has a value. When `false`, the element
     * is always visible.
     * @default false
     */
    showOnFocus: boolean;
  };
};

type InputReactAttributes = Omit<
  React.ComponentProps<'input'>,
  'id' | 'prefix'
>;

interface InputProps extends InputReactAttributes {
  /**
   * A boolean flag that indicates if the input is in an invalid state.
   * When set to `true`, the input will be visually highlighted as invalid.
   * @default false
   */
  invalid?: boolean;

  /**
   * Unique identifier for the input field. Used for label association and accessibility compliance.
   */
  id: string;

  /**
   * If true, the label will be visually hidden but still accessible for screen readers.
   * @default false
   */
  hideLabel?: boolean;

  /**
   * The label for the input field, providing context to the user about what information should be entered.
   */
  label: string;

  /**
   * A node or component that will be rendered after the input field content.
   * Can also accept configuration options such as showOnFocus.
   * See {@link PrefixAndSuffixBaseProps} for more details.
   */
  suffix?: PrefixAndSuffixBaseProps & SuffixOptions;

  /**
   * A node or component that will be rendered before the input field content.
   * Can also accept configuration options such as showOnFocus.
   * See {@link PrefixAndSuffixBaseProps} for more details.
   */
  prefix?: PrefixAndSuffixBaseProps;

  /**
   * Optional descriptive text displayed below the input field.
   * Useful for providing additional guidance or validation feedback.
   */
  helperText?: string;
}

/**
 * Collect user input with enhanced text fields that support prefixes, suffixes, and comprehensive validation feedback.
 *
 * @param props - Input properties. See {@link InputProps} for detailed prop documentation.
 * @param ref - Forwarded ref to the input element
 * @returns Rendered input component
 *
 * This component is **uncontrolled** by default, meaning it does not manage
 * its internal state but relies on the defaultValue prop to set
 * the initial value. The value of the input is not internally
 * tracked by the component, which makes it suitable for use cases where
 * the parent component manages the state of the form.
 *
 * @example
 * ```tsx
 * // Basic input
 * <Input id="email" label="Email Address" />
 *
 * // Input with prefix and suffix
 * <Input
 *   id="search"
 *   label="Search"
 *   prefix={{ component: <SearchIcon /> }}
 *   suffix={{ component: <ClearIcon /> }}
 * />
 *
 * // Invalid input with helper text
 * <Input
 *   id="password"
 *   label="Password"
 *   type="password"
 *   invalid
 *   helperText="Password must be at least 8 characters"
 * />
 * ```
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type,
      invalid,
      suffix,
      prefix,
      label,
      hideLabel,
      helperText,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={cn('flex w-full flex-col gap-1', {})}>
        <Label
          className={cn({
            'text-foreground/50 hover:cursor-not-allowed': props.disabled,
            'visibility-hidden': hideLabel,
          })}
          htmlFor={props.id}
          data-testid="input-component-label"
        >
          {label}
        </Label>
        <div className="flex flex-col gap-2">
          <div
            className={cn(
              'group/input mb-0 flex w-full items-center justify-between gap-2 rounded-xs border border-solid border-input bg-background px-3 py-2 leading-[20px] text-foreground/95 transition-all duration-300 ease-in-out focus-within:border-ring focus-within:shadow-input hover:border-input-hover hover:focus-within:border-ring',
              {
                'border-warning shadow-none focus-within:border-warning focus-within:shadow-none hover:border-warning hover:shadow-none hover:focus-within:border-warning':
                  invalid,
                'hover:border-input-hover-opacity-50 disabled:cursor-not-allowed':
                  props.disabled,
              },
            )}
          >
            {prefix && prefix.component && (
              <div
                className={cn('flex', {
                  'w-5': React.isValidElement(prefix.component),
                  'w-fit': !React.isValidElement(prefix.component),
                })}
              >
                {prefix.component}
              </div>
            )}

            <input
              type={type}
              name={props.id}
              ref={ref}
              className={cn(
                className,
                'peer m-0 h-full w-full rounded-none border-none p-0 font-nunito text-sm text-foreground/95 placeholder:text-muted-foreground/90 focus:border-none focus:shadow-none focus:outline-hidden disabled:bg-transparent disabled:text-foreground/20',
                {
                  'placeholder:text-warning': invalid,
                },
              )}
              aria-describedby={helperText && `helper-text-input-${props.id}`}
              aria-invalid={invalid}
              {...props}
            />
            {suffix && suffix.component && (
              <div
                className={cn('flex', {
                  'w-5': React.isValidElement(suffix.component),
                  'w-fit': !React.isValidElement(suffix.component),
                  'opacity-0 transition-opacity duration-300 ease-in-out peer-focus:opacity-100':
                    suffix.options?.showOnFocus,
                })}
              >
                {suffix.component}
              </div>
            )}
          </div>
          {helperText && (
            <span
              id={`helper-text-input-${props.id}`}
              className={cn(['text-sm', 'text-foreground/50'], {
                'text-warning': invalid,
              })}
            >
              {helperText}
            </span>
          )}
        </div>
      </div>
    );
  },
);
Input.displayName = 'Input';

export default Input;
